import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getLeadDocuments, getDocumentVerificationHistory } from "services/leadsService";
import { getDocumentFolders, DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";

const fetchDocuments = async (params) => {
  try {
    const isHistoryView = params.viewType === "history";
    if (isHistoryView) {
      const response = await getDocumentVerificationHistory({
        leadId: params.leadId,
        folderId: null,
        fileType: params.fileType,
        search: params.search,
        startDate: params.startDate,
        endDate: params.endDate,
        modifiedStartDate: params.modifiedStartDate,
        modifiedEndDate: params.modifiedEndDate,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      });

      if (response.success) {
        const transformedData = response.data.map((doc) => ({
          id: doc.DocumentId,
          name: doc.FileName,
          fileName: doc.FileName,
          filePath: doc.FilePath,
          size: doc.FileSize * 1024,
          fileType: doc.FileName?.split(".").pop()?.toLowerCase() || "unknown",
          uploadedAt: doc.UploadedOn,
          uploadedBy: doc.UploadedByName,
          verificationStatus: doc.VerificationStatus,
          remarks: doc.Remark,
          canApproveOrReject: doc.CanApproveOrReject,
          leadName: doc.LeadName,
          isHistoryItem: true,
          leadId: params.leadId,
          DocumentId: doc.DocumentId,
          FileName: doc.FileName,
          FileExtension: doc.FileExtension,
          FilePath: doc.FilePath,
          LeadName: doc.LeadName,
          History: doc.History || [],
        }));

        return {
          data: transformedData,
          totalCount: (params.pageNumber === 1 && transformedData.length === 0) ? 0 : (response.totalRecords ?? 0),
          hasMore: transformedData.length >= params.pageSize,
          folders: [],
          currentFolderId: null,
          parentFolderId: null,
        };
      } else {
        throw new Error(response.error);
      }
    } else {
      // Use new generic DocumentFolders service
      const response = await getDocumentFolders({
        entityType: DOCUMENT_ENTITY_TYPES.LEAD,
        entityId: params.leadId,
        folderId: params.folderId,
        fileType: params.fileType,
        search: params.search,
        startDate: params.startDate,
        endDate: params.endDate,
        modifiedStartDate: params.modifiedStartDate,
        modifiedEndDate: params.modifiedEndDate,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      });

      if (response.success) {
        let finalData = response.data || [];
        const subfolderIds = new Set((response.folders || []).map((f) => String(f.FolderId || f.folderId || f.id)));
        const hasSearchOrFilters = Boolean(
          params.fileType ||
            params.search ||
            params.startDate ||
            params.endDate ||
            params.modifiedStartDate ||
            params.modifiedEndDate
        );

        if (!hasSearchOrFilters) {
          const currentTargetFolderId = params.folderId ? String(params.folderId) : null;
          finalData = (response.data || []).filter((doc) => {
            const docFolderId = doc.folderId ?? doc.FolderId ?? null;
            const docFolderIdStr = docFolderId !== null && docFolderId !== undefined ? String(docFolderId) : null;

            if (docFolderIdStr && subfolderIds.has(docFolderIdStr)) {
              return false;
            }

            if (currentTargetFolderId) {
              if (docFolderIdStr) {
                return docFolderIdStr === currentTargetFolderId;
              }
              return true;
            }

            if (!currentTargetFolderId) {
              if (docFolderIdStr && docFolderIdStr !== "null" && docFolderIdStr !== String(response.currentFolderId)) {
                return false;
              }
            }

            return true;
          });
        }

        return {
          data: finalData,
          totalCount: (params.pageNumber === 1 && finalData.length === 0) ? 0 : (response.totalRecords ?? finalData.length),
          hasMore: finalData.length >= params.pageSize,
          folders: response.folders || [],
          currentFolderId: response.currentFolderId || null,
          parentFolderId: response.parentFolderId || null,
        };
      } else {
        // Fallback to legacy getLeadDocuments if new endpoint unavailable
        const legacyResponse = await getLeadDocuments(params);
        if (legacyResponse.success) {
          const transformedData = legacyResponse.data.map((doc) => ({
            id: doc.DocumentId,
            name: doc.FileName,
            fileType: doc.FileName.split(".").pop().toLowerCase(),
            size: doc.FileSize * 1024,
            uploadedBy: doc.UploadedByName,
            uploadedAt: doc.UploadedOn,
            leadId: params.leadId,
            filePath: doc.FilePath,
            isVerified: doc.IsVerified ?? doc.IsVerfied ?? false,
            docVerificationId: doc.DocVerificationId,
            canApproveOrReject: doc.CanApproveOrReject,
            verificationStatus:
              typeof doc.VerificationStatus === "number"
                ? doc.VerificationStatus
                : parseInt(doc.VerificationStatus ?? 0, 10) || 0,
            verifiedBy:
              doc.VerifiedByName || doc.VerifiedBy || doc.VerifiedById || null,
            verifiedAt: doc.VerifiedAt || doc.VerifiedDate || null,
            verificationRemark:
              doc.Remark || doc.Remarks || doc.VerificationRemark || "",
            isHistoryItem: false,
          }));

          return {
            data: transformedData,
            totalCount: (params.pageNumber === 1 && transformedData.length === 0) ? 0 : (legacyResponse.totalRecords ?? 0),
            hasMore: transformedData.length >= params.pageSize,
            folders: legacyResponse.folders || [],
            currentFolderId: legacyResponse.currentFolderId || null,
            parentFolderId: legacyResponse.parentFolderId || null,
          };
        }
        throw new Error(response.error || legacyResponse.error);
      }
    }
  } catch (error) {
    console.error("Error fetching documents:", error);
    return {
      data: [],
      totalCount: 0,
      hasMore: false,
      folders: [],
      currentFolderId: null,
      parentFolderId: null,
    };
  }
};

export const useDocuments = (leadId, initialViewType = "documents") => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [parentFolderId, setParentFolderId] = useState(null);
  const [rootFolderId, setRootFolderId] = useState(null);

  // Client-side Key-Value cache mapping folderId -> { data, totalCount, folders, currentFolderId, parentFolderId }
  const folderCacheRef = useRef({});

  // Use a ref for hasInitialData so loadDocuments always reads the latest value
  const hasInitialDataRef = useRef(false);
  const [hasInitialData, setHasInitialDataState] = useState(false);
  const [viewType, setViewType] = useState(initialViewType);

  const setHasInitialData = useCallback((value) => {
    hasInitialDataRef.current = value;
    setHasInitialDataState(value);
  }, []);

  const invalidateCache = useCallback((targetFolderId = null) => {
    folderCacheRef.current = {};
  }, []);

  const [filterState, setFilterState] = useState({
    leadId: leadId,
    folderId: null,
    fileType: null,
    search: "",
    startDate: null,
    endDate: null,
    modifiedStartDate: null,
    modifiedEndDate: null,
    pageNumber: 1,
    pageSize: 10,
    viewType: initialViewType,
  });

  // Stable ref for filterState so loadDocuments always reads the latest
  const filterStateRef = useRef(filterState);
  useEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filterState.fileType ||
        filterState.search ||
        filterState.startDate ||
        filterState.endDate ||
        filterState.modifiedStartDate ||
        filterState.modifiedEndDate
    );
  }, [
    filterState.fileType,
    filterState.search,
    filterState.startDate,
    filterState.endDate,
    filterState.modifiedStartDate,
    filterState.modifiedEndDate,
  ]);

  // loadDocuments reads from refs to always get latest values
  const loadDocuments = useCallback(async (options = {}) => {
    const { forceRefresh = false } = options || {};
    const params = filterStateRef.current;
    if (!params.leadId) return;

    const cacheKey = params.folderId ? String(params.folderId) : "ROOT";
    const isUnfiltered =
      !params.fileType &&
      !params.search &&
      !params.startDate &&
      !params.endDate &&
      !params.modifiedStartDate &&
      !params.modifiedEndDate &&
      params.pageNumber === 1;

    // Check client-side Key-Value cache for un-filtered requests
    if (
      !forceRefresh &&
      params.viewType === "documents" &&
      isUnfiltered &&
      folderCacheRef.current[cacheKey]
    ) {
      const cached = folderCacheRef.current[cacheKey];
      setDocuments(cached.data);
      setTotalRecords(cached.totalCount);
      setFolders(cached.folders);
      setCurrentFolderId(cached.currentFolderId);
      setParentFolderId(cached.parentFolderId);
      const hasCachedItems = cached.totalCount > 0 || (cached.folders && cached.folders.length > 0);
      if (hasCachedItems && !hasInitialDataRef.current) {
        setHasInitialData(true);
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchDocuments(params);

      // Prevent race conditions
      const currentParams = filterStateRef.current;
      const currentFileTypeStr = Array.isArray(currentParams.fileType)
        ? currentParams.fileType.join(",")
        : currentParams.fileType || "";
      const paramsFileTypeStr = Array.isArray(params.fileType)
        ? params.fileType.join(",")
        : params.fileType || "";

      if (
        currentParams.leadId !== params.leadId ||
        currentParams.folderId !== params.folderId ||
        currentParams.viewType !== params.viewType ||
        currentParams.pageNumber !== params.pageNumber ||
        currentParams.pageSize !== params.pageSize ||
        currentFileTypeStr !== paramsFileTypeStr ||
        currentParams.search !== params.search ||
        currentParams.startDate !== params.startDate ||
        currentParams.endDate !== params.endDate ||
        currentParams.modifiedStartDate !== params.modifiedStartDate ||
        currentParams.modifiedEndDate !== params.modifiedEndDate
      ) {
        return;
      }

      setDocuments(response.data);
      setTotalRecords(response.totalCount);
      setFolders(response.folders);
      setCurrentFolderId(response.currentFolderId);
      setParentFolderId(response.parentFolderId);

      if (response.currentFolderId) {
        if (!params.folderId) {
          setRootFolderId(response.currentFolderId);
          setFolderPath((prev) => {
            if (prev.length > 0 && prev[0].id === null) {
              const updated = [...prev];
              updated[0] = { ...updated[0], id: response.currentFolderId };
              return updated;
            }
            return prev;
          });
        }
      }

      // Store in Key-Value cache for un-filtered requests
      if (params.viewType === "documents" && isUnfiltered) {
        folderCacheRef.current[cacheKey] = {
          data: response.data,
          totalCount: response.totalCount,
          folders: response.folders,
          currentFolderId: response.currentFolderId,
          parentFolderId: response.parentFolderId,
        };
      }

      const hasFilters =
        params.fileType ||
        params.search ||
        params.startDate ||
        params.endDate ||
        params.modifiedStartDate ||
        params.modifiedEndDate;
      const hasAnyContent =
        response.totalCount > 0 || (response.folders && response.folders.length > 0);
      if (!hasFilters) {
        setHasInitialData(hasAnyContent);
      } else if (hasAnyContent && !hasInitialDataRef.current) {
        setHasInitialData(true);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching documents:", error);
      const currentParams = filterStateRef.current;
      if (
        currentParams.leadId === params.leadId &&
        currentParams.folderId === params.folderId &&
        currentParams.viewType === params.viewType &&
        currentParams.pageNumber === params.pageNumber &&
        currentParams.pageSize === params.pageSize &&
        currentParams.fileType === params.fileType &&
        currentParams.search === params.search &&
        currentParams.startDate === params.startDate &&
        currentParams.endDate === params.endDate &&
        currentParams.modifiedStartDate === params.modifiedStartDate &&
        currentParams.modifiedEndDate === params.modifiedEndDate
      ) {
        setLoading(false);
      }
    }
  }, [setHasInitialData]);

  // Stable refetch that invalidates current cache key and re-fetches
  const refetch = useCallback(
    (force = true) => {
      if (force) {
        invalidateCache(filterStateRef.current.folderId);
      }
      return loadDocuments({ forceRefresh: force });
    },
    [invalidateCache, loadDocuments]
  );

  // Re-fetch whenever filterState changes
  useEffect(() => {
    loadDocuments();
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  const [folderPath, setFolderPath] = useState([{ id: null, name: "Root" }]);

  // Reset all state when leadId changes
  useEffect(() => {
    const prevId = filterStateRef.current.leadId
      ? String(filterStateRef.current.leadId)
      : "";
    const nextId = leadId ? String(leadId) : "";

    if (prevId !== nextId) {
      folderCacheRef.current = {};
      setDocuments([]);
      setTotalRecords(0);
      setHasInitialData(false);
      setFolders([]);
      setCurrentFolderId(null);
      setParentFolderId(null);
      setFolderPath([{ id: null, name: "Root" }]);
      setFilterState({
        leadId: leadId,
        folderId: null,
        fileType: null,
        search: "",
        startDate: null,
        endDate: null,
        pageNumber: 1,
        pageSize: 10,
        viewType: initialViewType,
      });
    }
  }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToFolder = useCallback((folderId, folderName) => {
    setFolderPath((prevPath) => {
      const existingIndex = prevPath.findIndex((item) => item.id === folderId);
      if (existingIndex !== -1) {
        return prevPath.slice(0, existingIndex + 1);
      }
      return [...prevPath, { id: folderId, name: folderName || "Folder" }];
    });
    setFilterState((prev) => ({ ...prev, folderId: folderId || null, pageNumber: 1 }));
  }, []);

  const navigateToBreadcrumb = useCallback((index) => {
    setFolderPath((prevPath) => {
      if (index < 0 || index >= prevPath.length) return prevPath;
      const target = prevPath[index];
      setFilterState((prev) => ({ ...prev, folderId: target.id || null, pageNumber: 1 }));
      return prevPath.slice(0, index + 1);
    });
  }, []);

  const navigateUp = useCallback(() => {
    setFolderPath((prevPath) => {
      if (prevPath.length > 1) {
        const newPath = prevPath.slice(0, prevPath.length - 1);
        const target = newPath[newPath.length - 1];
        setFilterState((prev) => ({ ...prev, folderId: target.id || null, pageNumber: 1 }));
        return newPath;
      } else {
        setFilterState((prev) => ({ ...prev, folderId: parentFolderId, pageNumber: 1 }));
        return [{ id: null, name: "Root" }];
      }
    });
  }, [parentFolderId]);

  const handleFilterChange = useCallback((keyOrObj, value) => {
    setFilterState((prev) => {
      let updates = {};
      if (typeof keyOrObj === "object" && keyOrObj !== null) {
        updates = keyOrObj;
      } else {
        updates = { [keyOrObj]: value };
      }

      let hasChanges = false;
      const nextState = { ...prev };

      Object.entries(updates).forEach(([k, val]) => {
        let normVal = Array.isArray(val) ? val.join(",") : val === "" ? null : val;
        if (k === "fileType" && typeof normVal === "string") {
          normVal = normVal.replace(/^\./, "");
        }
        if (prev[k] !== normVal) {
          nextState[k] = normVal;
          hasChanges = true;
        }
      });

      if (!hasChanges) return prev;
      nextState.pageNumber = 1;
      return nextState;
    });
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilterState((prev) => ({ ...prev, pageNumber: page }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState((prev) => ({
      ...prev,
      fileType: null,
      search: "",
      startDate: null,
      endDate: null,
      modifiedStartDate: null,
      modifiedEndDate: null,
      pageNumber: 1,
    }));

    const searchInput = document.querySelector(
      'input[placeholder="Search documents..."]',
    );
    if (searchInput) searchInput.value = "";
  }, []);

  const switchView = useCallback((newViewType) => {
    setViewType(newViewType);
    setFilterState((prev) => ({
      ...prev,
      viewType: newViewType,
      fileType: null,
      search: "",
      startDate: null,
      endDate: null,
      modifiedStartDate: null,
      modifiedEndDate: null,
      pageNumber: 1,
    }));

    const searchInput = document.querySelector(
      'input[placeholder="Search documents..."]',
    );
    if (searchInput) searchInput.value = "";
  }, []);

  return {
    documents,
    loading,
    totalRecords,
    hasInitialData,
    hasActiveFilters,
    filterState,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    switchView,
    viewType,
    totalPages: Math.ceil(totalRecords / filterState.pageSize),
    refetch,
    invalidateCache,
    folders,
    currentFolderId,
    parentFolderId,
    rootFolderId,
    folderPath,
    navigateToFolder,
    navigateToBreadcrumb,
    navigateUp,
  };
};
